import { useEffect, useMemo } from "react";
import {
  useRive,
  useStateMachineInput,
} from "@rive-app/react-webgl2";
import type {
  NixiActivity,
  NixiAnimationState,
  NixiColour,
  NixiContext,
  NixiExpression,
  NixiVariant,
} from "./NixiCompanion";

type NixiRiveRendererProps = {
  src: string;
  state: NixiAnimationState;
  expression: NixiExpression;
  context: NixiContext;
  colour: NixiColour;
  variant: NixiVariant;
  activity: NixiActivity;
  reducedMotion: boolean;
  label: string;
  onReady: () => void;
  onError: () => void;
};

function animationFor(
  state: NixiAnimationState,
  context: NixiContext,
  activity: NixiActivity,
) {
  const routineAnimation: Partial<Record<NixiActivity, string>> = {
    toilet: "ToiletWalk",
    gettingDressed: "GettingDressed",
    dressed: "PackBag",
    eating: "Breakfast",
    brushingTeeth: "BrushTeeth",
    packingBag: "PackBag",
    shoes: "PutOnShoes",
    preschool: "Preschool",
    snack: "Snack",
    play: "Play",
    dinner: "Dinner",
    gettingPajamas: "GetPajamas",
    bedtime: "Bedtime",
    bedtimeComplete: "Bedtime",
    flyComplete: "Complete",
  };
  if (routineAnimation[activity]) return routineAnimation[activity];
  if (state === "celebrate" || context === "celebration") return "Timeline 3";
  if (context === "morning" || context === "routine" || context === "task")
    return "Timeline 2";
  return "Timeline 1";
}

export function NixiRiveRenderer({
  src,
  state,
  context,
  activity,
  reducedMotion,
  label,
  onReady,
  onError,
}: NixiRiveRendererProps) {
  const isGirlMaster = src.includes("nixi-girl-master-v7.riv");
  const isBoyMaster = src.includes("nixi-boy-master-v1.riv");
  const isCompanionMaster = isGirlMaster || isBoyMaster;
  const isGirlActionMaster = src.includes("nixi-girl-actions-v8.riv");
  const isGirlToilet = src.includes("nixi-girl-toilet-v8.riv");
  const isGirlBedtime = src.includes("nixi-girl-bedtime-v9.riv");
  const isLayeredHome = src.includes("nixi-home-girl-layered-v1.riv");
  const isLayeredBrush = src.includes("nixi-routine-brush-girl-v3.riv");
  const isMyDayV5 = src.includes("nixi-myday-");
  const myDayKind = isMyDayV5
    ? src.match(/nixi-myday-(preschool|snack|play|dinner|bedtime)-girl-v5\.riv/)?.[1]
    : undefined;
  const myDayAnimation = myDayKind
    ? `${myDayKind.charAt(0).toUpperCase()}${myDayKind.slice(1)}Rigged`
    : undefined;
  const myDayArtboard = myDayKind
    ? `NIXI My Day ${myDayKind.charAt(0).toUpperCase()}${myDayKind.slice(1)}`
    : undefined;
  const isLayeredRoutine = /nixi-routine-.+-girl-v[234]\.riv$/.test(src) || isMyDayV5;
  const animation = useMemo(
    () => isLayeredBrush
      ? "BrushTeethRigged"
      : isMyDayV5 && myDayAnimation
        ? myDayAnimation
        : animationFor(state, context, activity),
    [activity, isLayeredBrush, isMyDayV5, myDayAnimation, state, context],
  );
  const { rive, RiveComponent } = useRive(
    {
      src,
      artboard: isGirlBedtime
        ? "NIXI Bedtime"
        : isGirlToilet
        ? "NIXI Toilet"
        : isGirlActionMaster
        ? "NIXI Actions"
        : isGirlMaster
        ? "NIXI"
        : isLayeredHome
        ? "NIXI Home"
        : isMyDayV5 && myDayArtboard
          ? myDayArtboard
        : isLayeredRoutine
          ? isLayeredBrush ? "NIXI Brush Rig" : "NIXI Routine"
          : "NIXI",
      animations: isLayeredHome || isCompanionMaster ? undefined : animation,
      stateMachines: isCompanionMaster
        ? "NIXI Companion"
        : isLayeredHome
          ? "NIXI Controller"
          : undefined,
      autoplay: !reducedMotion,
      onLoad: onReady,
      onLoadError: onError,
    },
    {
      shouldResizeCanvasToContainer: true,
    },
  );
  const controllerName = isCompanionMaster ? "NIXI Companion" : "NIXI Controller";
  const wave = useStateMachineInput(rive, controllerName, "wave");
  const flutter = useStateMachineInput(rive, controllerName, "flutter");
  const tickle = useStateMachineInput(rive, controllerName, "tickle");
  const celebrate = useStateMachineInput(
    rive,
    controllerName,
    "celebrate",
  );
  const feed = useStateMachineInput(rive, controllerName, "feed");

  useEffect(() => {
    if (!rive || isLayeredHome || isCompanionMaster) return;
    // Rive releases its WASM artboard as soon as a route swaps renderers. A
    // synchronous play during that same React commit can otherwise target the
    // just-deleted artboard and crash the whole JNR profile. Defer one frame
    // and cancel on cleanup so only the live renderer receives the command.
    let cancelled = false;
    const frame = window.requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        rive.stop();
        if (!reducedMotion) rive.play(animation);
      } catch {
        // The replacement renderer created by the current route owns playback.
      }
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [animation, isCompanionMaster, isLayeredHome, reducedMotion, rive]);

  useEffect(() => {
    if ((!isLayeredHome && !isCompanionMaster) || context !== "home" || reducedMotion || !rive) return;
    const timer = window.setTimeout(() => wave?.fire(), 700);
    return () => window.clearTimeout(timer);
  }, [context, isCompanionMaster, isLayeredHome, reducedMotion, rive, wave]);

  useEffect(() => {
    if (!isBoyMaster || context === "home" || reducedMotion || !rive) return;
    const timer = window.setTimeout(() => {
      if (["eating", "snack", "dinner"].includes(activity)) feed?.fire();
      else if (["play", "flyComplete"].includes(activity)) celebrate?.fire();
      else if (["preschool", "packingBag", "shoes"].includes(activity)) flutter?.fire();
      else wave?.fire();
    }, 450);
    return () => window.clearTimeout(timer);
  }, [activity, celebrate, context, feed, flutter, isBoyMaster, reducedMotion, rive, wave]);

  useEffect(() => {
    if ((!isLayeredHome && !isCompanionMaster) || reducedMotion) return;
    if (state === "wave") wave?.fire();
    if (state === "wingFlutter") flutter?.fire();
    if (state === "celebrate") celebrate?.fire();
    if (state === "feed") feed?.fire();
  }, [celebrate, feed, flutter, isCompanionMaster, isLayeredHome, reducedMotion, state, wave]);

  return (
    <span
      className="jnr-nixi-rive block h-full w-full overflow-visible bg-transparent"
      role="img"
      aria-label={label}
      data-rive-animation={isCompanionMaster ? "NIXI Companion" : isLayeredHome ? "NIXI Controller" : animation}
      onClick={() => (isLayeredHome || isCompanionMaster) && !reducedMotion && tickle?.fire()}
    >
      <RiveComponent className="h-full w-full" />
    </span>
  );
}

